<template>
  <div :class="classes" :data-aos="item.animation">
    <div class="bravado-card__header">
      <div v-if="!$slots['text-image']" class="bravado-card__image">
        <img
          v-if="item.image"
          :width="size"
          :height="size"
          :src="item.image"
          :alt="item.title"
          class="bravado-card__image--picture"
        />
        <img v-else-if="item.logo" :width="size" :height="size" :src="item.logo" :alt="item.title" />
      </div>

      <div class="bravado-card__text">
        <a
          v-if="item.link"
          :href="item.link"
          target="_blank"
          rel="nofollow"
          :title="item.title"
          class="bravado-card__title"
        >
          <q>
            {{ item.title }}
          </q>
        </a>
        <h3 v-else class="bravado-card__title">
          {{ item.title }}
        </h3>

        <slot name="text-image" />

        <p class="bravado-card__subtitle" :class="$slots['text-image'] ? 'bravado-card__subtitle--image' : ''">
          {{ item.subtitle }}
        </p>
      </div>
    </div>

    <div v-if="item.points || item.badge" class="bravado-card__content">
      <ul v-if="item.points" class="bravado-card__points" role="list">
        <li v-for="(point, key) in item.points" :key="key">
          <img src="~/assets/svg/icon-check.svg" height="8" width="10" class="bravado-card__points-check" />
          <span>{{ point }}</span>
        </li>
      </ul>

      <div v-if="item.badge" class="bravado-card__badge">
        {{ item.badge }}
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import type { PropOptions, VueConstructor } from 'vue';
import Vue from 'vue';

export type BravadoCardItem = {
  logo?: VueConstructor<Vue>;
  image?: string;
  title: string;
  subtitle: string;
  color:
    | 'platinum-linear'
    | 'silver-linear'
    | 'gold-linear'
    | 'purple-linear'
    | 'cyan-gradient'
    | 'purple-hover'
    | 'purple-1'
    | 'purple-3'
    | 'purple-4'
    | 'cyan-0'
    | 'cyan-1'
    | 'cyan-2'
    | 'cyan-3'
    | 'brand';
  badge?: string;
  points?: string[];
  animation?: string;
  link?: string;
};

type BravadoCardProps = {
  item: BravadoCardItem;
  size: number;
  variant: 'primary' | 'secondary';
};

export default Vue.extend({
  name: 'BravadoItemCard',
  props: {
    item: {
      type: Object,
      required: true,
    } as PropOptions<BravadoCardProps['item']>,
    variant: {
      type: String,
      default: 'primary',
    } as PropOptions<BravadoCardProps['variant']>,
    size: {
      type: Number,
      default: 30,
    } as PropOptions<BravadoCardProps['size']>,
  },
  computed: {
    classes() {
      const colorClass = this.item.color ? ` bravado-card--${this.item.color}` : '';
      const variantClass = this.variant ? `bravado-card--${this.variant}` : '';

      return `bravado-card ${colorClass} ${variantClass}`;
    },
  },
});
</script>

<style lang="scss" scoped>
.bravado-card {
  box-shadow: inset -1px -1px 0 #16161c, inset 1px 1px 0 rgba(255, 255, 255, 0.05);
  border-radius: 12px;
  width: 100%;
  max-width: 343px;
  color: rgba(var(--white));
  padding: 38px 28px 30px;
  position: relative;
  overflow: hidden;
  text-align: left;
  display: flex;
  flex-direction: column;
  z-index: 1;

  &::before,
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
  }

  &::before {
    height: 7px;
  }

  &__header {
    display: flex;
    gap: 12px;
    align-items: center;

    @include mq('sm') {
      gap: 25px;
    }
  }

  &__image {
    height: 53px;
    width: 53px;
    min-height: 53px;
    min-width: 53px;

    &--picture {
      height: auto;
    }

    img {
      width: 100%;
    }
  }

  &__text {
    text-align: left;
  }

  &__title {
    font-weight: 600;
    font-size: 17px;
    line-height: 24px;
    color: rgba(var(--white));

    @include mq('sm') {
      gap: 25px;
    }
  }

  &__subtitle {
    font-weight: 500;
    font-size: 11px;
    line-height: 14px;
    display: flex;
    align-items: center;
    color: var(--gray-50);
    text-transform: uppercase;
  }

  &__content {
    font-weight: 400;
    font-size: 13px;
    line-height: 17px;
    color: var(--theme-color-sub-text);
    padding: 10px 0 0;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    flex-grow: 1;

    @include mq('sm') {
      padding-top: 20px;
    }
  }

  &__points {
    padding-left: 20px;

    &-check {
      margin: 2px 10px 0 -18px;
    }

    li {
      margin-bottom: 16px;
      display: flex;
      align-items: center;
    }
  }

  &__badge {
    background: var(--theme-color-sub-text);
    box-shadow: var(--theme-elevation-01);
    border-radius: 12px;
    font-weight: 400;
    font-size: 15px;
    line-height: 24px;
    color: var(--gray-20);
    width: fit-content;
    padding: 4px 8px;
    display: inline-flex;

    @include mq('sm') {
      margin-top: 18px;
    }
  }

  &--primary {
    background-color: var(--gray-80);

    &::after {
      height: 100%;
      background-color: var(--theme-color-card);
      z-index: -1;
      transition: opacity ease-in-out 0.1s;
    }

    &:hover {
      &::after {
        opacity: 0;
      }
    }

    @include mq('sm') {
      min-height: 180px;
    }

    .bravado-card {
      &__header {
        align-items: flex-start;

        @include mq('sm') {
          gap: 20px;
        }
      }

      &__image {
        height: 42px;
        width: 42px;
        min-height: 42px;
        min-width: 42px;

        @include mq('sm') {
          height: 53px;
          width: 53px;
          min-height: 53px;
          min-width: 53px;
        }
      }

      &__text {
        height: 100%;
        align-items: flex-start;
        display: flex;
        flex-direction: column;
        justify-content: space-evenly;
      }

      &__title {
        font-weight: 600;
        font-size: 13px;
        line-height: 20px;

        @include mq('sm') {
          font-size: 15px;
          line-height: 24px;
        }
      }
    }
  }

  &--secondary {
    background-color: var(--gray-80);

    &::after {
      height: 100%;
      background-color: var(--theme-color-card);
      z-index: -1;
      transition: opacity ease-in-out 0.1s;
    }

    &:hover {
      &::after {
        opacity: 0;
      }
    }

    @include mq('sm') {
      min-height: 180px;
    }

    .bravado-card {
      &__header {
        height: 100%;
        align-items: flex-start;

        @include mq('sm') {
          gap: 20px;
        }
      }

      &__image {
        height: 42px;
        width: 42px;
        min-height: 42px;
        min-width: 42px;

        @include mq('sm') {
          height: 53px;
          width: 53px;
          min-height: 53px;
          min-width: 53px;
        }
      }

      &__text {
        height: 100%;
        align-items: flex-start;
        display: flex;
        flex-direction: column;
        justify-content: space-between;
      }

      &__title {
        font-weight: 600;
        font-size: 13px;
        line-height: 20px;
        margin-bottom: 10px;

        @include mq('sm') {
          font-size: 15px;
          line-height: 24px;
        }
      }
    }
  }

  &--sss {
    box-shadow: 0 4px 6px rgba(129, 132, 150, 0.15);
    //background: rgba(255, 255, 255, .3);

    min-height: 232px;
    min-width: 192px;
    max-width: 297px;
    padding: 30px 8px 0;
    height: 100%;

    &::after {
      height: 100%;
      background-color: rgba(var(--white));
      z-index: -1;
      transition: opacity ease-in-out 0.1s;
    }

    &:hover {
      &::after {
        opacity: 1;
      }
    }

    @include mq('sm') {
      padding: 38px 28px 24px;
      min-width: 220px;
      min-height: 247px;
    }
    //@include mq('sm') {
    //
    //}

    .bravado-card {
      &__header {
        height: auto;
        align-items: center;
        justify-content: center;
        flex-direction: column;
        text-align: center;

        @include mq('sm') {
          gap: 0;
        }
      }

      &__image {
        height: 58px;
        width: 58px;
        border-radius: 12px;
        display: flex;
        align-items: center;
        justify-content: center;
        min-height: 42px;
        min-width: 42px;
        margin: 7px 0 17px;

        img {
          filter: brightness(0) invert(1);
        }

        @include mq('sm') {
          height: 68px;
          width: 68px;
          min-height: 68px;
          min-width: 68px;
          margin: 7px 0 29px;
        }
      }

      &__text {
        height: auto;
        align-items: center;
        flex-direction: column;
        justify-content: center;
        text-align: center;
      }

      &__title {
        color: #000;
        min-height: 55px;
        font-size: 17px;
        line-height: 26px;

        @include mq('sm') {
          @include bravado-h5;
        }
      }

      &__subtitle {
        margin: -18px 0 -10px;
        text-transform: none;
        justify-content: center;

        @include body;

        @include mq('sm') {
          margin: -16px 0 -4px;

          @include bravado-headline;
        }

        &--image {
          font-size: 13px;
          line-height: 20px;

          @include mq('sm') {
            @include bravado-headline;
          }
        }
      }
    }
  }

  &--platinum-linear {
    &::before {
      background: linear-gradient(
        104.06deg,
        #a0b9cc 17.79%,
        #a0b9cc 17.8%,
        #dde5f0 42.33%,
        #c5e6f9 62.94%,
        #a7b5bb 86.6%
      );
      transform: rotate(-180deg);
    }

    .bravado-card__points-check {
      background: linear-gradient(
        104.06deg,
        #a0b9cc 17.79%,
        #a0b9cc 17.8%,
        #dde5f0 42.33%,
        #c5e6f9 62.94%,
        #a7b5bb 86.6%
      );
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      color: #a0b9cc;
      filter: opacity(0.4) drop-shadow(0 0 0 #a0b9cc) brightness(3.25);
    }
  }

  &--silver-linear {
    &::before {
      background: linear-gradient(103.18deg, #bfc1c4 0%, #dadcde 34.11%, #e7e9ec 65.07%, #bcbebf 100%);
      transform: rotate(-180deg);
    }

    .bravado-card__points-check {
      background: linear-gradient(103.18deg, #bfc1c4 0%, #dadcde 34.11%, #e7e9ec 65.07%, #bcbebf 100%);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      color: #bfc1c4;
      filter: opacity(0.4) drop-shadow(0 0 0 #bfc1c4) brightness(3.25);
    }
  }

  &--gold-linear {
    &::before {
      background: linear-gradient(111.96deg, #e3bc5a 0%, #e9d8ab 25%, #f4e9c4 50%, #e9d8ab 75%, #e3bc5a 100%);
      transform: rotate(-180deg);
    }

    .bravado-card__points-check {
      background: linear-gradient(111.96deg, #e3bc5a 0%, #e9d8ab 25%, #f4e9c4 50%, #e9d8ab 75%, #e3bc5a 100%);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      color: #e3bc5a;
      filter: opacity(0.4) drop-shadow(0 0 0 #e3bc5a) brightness(3.25);
    }
  }

  &--purple-linear {
    &::before {
      background: linear-gradient(97.4deg, #77587c 0%, #888cc5 100%);
      transform: rotate(-180deg);
    }
  }

  &--cyan-gradient {
    &::before {
      background: var(--theme-cyan_hc_gradient);
      transform: rotate(-180deg);
    }
  }

  &--purple-hover {
    &::before {
      // FIXME [colors]: we don't have such color in dark theme, only in light
      // better to create palette al first

      background: linear-gradient(97.4deg, #824271 0%, #887688 100%);
      transform: rotate(-180deg);
    }
  }

  &--purple-0 {
    &::before {
      background: #5d3251;
    }

    &.bravado-card--sss .bravado-card__image {
      background: #5d3251;
    }
  }

  &--purple-1 {
    &::before {
      background: #5d3251;
    }

    &.bravado-card--sss .bravado-card__image {
      background: #5d3251;
    }
  }

  &--purple-3 {
    &::before {
      background: #af8fb6;
    }

    &.bravado-card--sss .bravado-card__image {
      background: #af8fb6;
    }
  }

  &--purple-4 {
    &::before {
      background: #dcd2dc;
    }

    &.bravado-card--sss .bravado-card__image {
      background: #dcd2dc;
    }
  }

  &--cyan-0 {
    &::before {
      background: #224373;
    }

    &.bravado-card--sss .bravado-card__image {
      background: #224373;
    }
  }

  &--cyan-1 {
    &::before {
      background: #8a92ce;
    }

    &.bravado-card--sss .bravado-card__image {
      background: #8a92ce;
    }
  }

  &--cyan-2 {
    &::before {
      background: #555fa3;
    }

    &.bravado-card--sss .bravado-card__image {
      background: #555fa3;
    }
  }

  &--cyan-3 {
    &::before {
      background: #a9b6f0;
    }

    &.bravado-card--sss .bravado-card__image {
      background: #a9b6f0;
    }
  }

  &--brand {
    &::before {
      background: #5e79c5;
    }

    &.bravado-card--sss .bravado-card__image {
      background: #5e79c5;
    }
  }
}

@media (min-width: 768px) {
  .bravado-card {
    &__header {
      gap: 25px;
    }

    &__title {
      font-size: 20px;
      line-height: 28px;
      letter-spacing: -0.3px;
    }

    &--alter {
      min-height: 180px;

      .bravado-card {
        &__title {
          font-size: 15px;
          line-height: 24px;
        }
      }
    }
  }
}
</style>
